import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { HandCoins } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { tk, fmtDate, todayStr } from "../lib/format";
import { useSettings } from "../context/SettingsContext";
import { PageHeader, Field, Empty } from "../components/kit";
import CustomerPicker from "../components/CustomerPicker";

export default function Collection() {
  const { settings } = useSettings();
  const PAY_METHODS = settings.pay_methods;
  const [sp] = useSearchParams();
  const [cust, setCust] = useState<any>(null);
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(todayStr());
  const [method, setMethod] = useState(PAY_METHODS[0]);
  const [note, setNote] = useState("");
  const [history, setHistory] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const cid = sp.get("customer");
    if (cid) api.get(`/customers/${cid}`).then((r) => setCust(r.data.customer)).catch(() => null);
  }, [sp]);

  const loadHistory = useCallback(() => {
    api.get("/payments", { params: { customer_id: cust?.id || "" } }).then((r) => setHistory(r.data));
  }, [cust]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const prev = cust?.due || 0;
  const remaining = prev - (+amount || 0);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cust) return toast.error("ক্রেতা বাছাই করুন");
    setBusy(true);
    try {
      const { data } = await api.post("/payments", { customer_id: cust.id, amount: +amount, date, method, note });
      toast.success(`${tk(data.amount)} আদায় হয়েছে · রশিদ ${data.receipt_no}`);
      setCust({ ...cust, due: data.remaining_due });
      setAmount("");
      setNote("");
    } catch (er) {
      toast.error(errMsg(er));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader title="টাকা আদায়" subtitle="ক্রেতা পরে টাকা দিলে এখানে এন্ট্রি দিন — বাকি স্বয়ংক্রিয়ভাবে কমবে" />
      <div className="grid lg:grid-cols-[420px_1fr] gap-4 items-start">
        <form onSubmit={submit} className="card p-5 space-y-4" data-testid="collection-form">
          <Field label="ক্রেতা"><CustomerPicker value={cust} onSelect={setCust} testid="collection-customer-search" /></Field>
          <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 flex justify-between">
            <span className="text-slate-600">আগের বাকি</span><b className="num text-red-600" data-testid="collection-previous-due">{tk(prev)}</b>
          </div>
          <Field label="আদায়ের পরিমাণ (৳)">
            <input className="inp num text-lg font-semibold" type="number" min="0" step="any" required value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="collection-amount-input" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="তারিখ"><input className="inp" type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="collection-date-input" /></Field>
            <Field label="মাধ্যম">
              <select className="inp" value={method} onChange={(e) => setMethod(e.target.value)} data-testid="collection-method-select">{PAY_METHODS.map((m) => <option key={m}>{m}</option>)}</select>
            </Field>
          </div>
          <Field label="নোট"><input className="inp" value={note} onChange={(e) => setNote(e.target.value)} data-testid="collection-note-input" /></Field>
          <div className={`rounded-lg px-4 py-3 flex justify-between ${remaining > 0 ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>
            <span className="font-semibold">বাকি থাকবে</span><b className="num text-lg" data-testid="collection-remaining-due">{tk(remaining)}</b>
          </div>
          <button className="btn btn-green w-full" disabled={busy || !cust || !(+amount > 0) || remaining < 0} data-testid="collection-submit-button">
            <HandCoins size={18} /> আদায় সেভ করুন
          </button>
        </form>
        <div className="card overflow-x-auto">
          <h2 className="text-lg font-semibold px-5 py-4 border-b border-slate-100">আদায়ের ইতিহাস {cust ? `— ${cust.name}` : "(সাম্প্রতিক)"}</h2>
          {history.length === 0 ? <Empty text="কোনো আদায় নেই" /> : (
            <table className="tbl" data-testid="collection-history-table">
              <thead><tr><th>তারিখ</th><th>রশিদ</th><th>ক্রেতা</th><th>মাধ্যম</th><th className="text-right">আদায়</th><th className="text-right">বাকি রইল</th></tr></thead>
              <tbody>
                {history.map((p) => (
                  <tr key={p.id}>
                    <td className="num">{fmtDate(p.date)}</td>
                    <td className="font-semibold">{p.receipt_no}</td>
                    <td>{p.customer_name}</td>
                    <td>{p.method}</td>
                    <td className="text-right num font-bold text-emerald-700">{tk(p.amount)}</td>
                    <td className="text-right num text-red-600">{tk(p.remaining_due)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
